import { Location } from '@angular/common';
import { ChangeDetectionStrategy, Component, OnDestroy } from '@angular/core';
import { BehaviorSubject, Subject, takeUntil } from 'rxjs';
import { randomId } from 'server/shared/helpers/random';
import {
  CustomCriteria,
  CustomCriteriaSelect,
  CustomCriteriaType,
} from 'server/shared/models/customCriteria';
import { Customer } from 'server/shared/models/customer';
import { CustomerService } from '../../services/customer.service';
import { PermissionsService } from '../../services/permissions.service';
import { ToastService } from '../../services/toast/toast.service';

type CustomCriteriaInCreation = {
  displayName: string;
  type: CustomCriteriaType;
  defaultValueOnJob?: boolean;
  defaultValueOnProfile?: boolean;
  blockingToggle?: boolean;
  multiple?: boolean;
  options?: { id: string; displayName: string }[];
  userEditable: boolean;
  enableOnJobRequest: boolean;
};

@Component({
  selector: 'admin-custom-criteria',
  templateUrl: 'custom-criteria.component.html',
  styleUrls: ['custom-criteria.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CustomCriteriaComponent implements OnDestroy {
  customer$ = new BehaviorSubject<Customer | undefined>(undefined);

  private destroyed$ = new Subject<void>();

  customCriteria$ = new BehaviorSubject<CustomCriteria[]>([]);
  initialCustomCriteria: CustomCriteria[] = [];
  customCriteriaInCreation$ = new BehaviorSubject<CustomCriteriaInCreation[]>(
    [],
  );

  readonly customCriteriaTypeEnum = CustomCriteriaType;

  constructor(
    private location: Location,
    private toastService: ToastService,
    private customerService: CustomerService,
    private permissionService: PermissionsService,
  ) {
    this.customerService.readyDispoCustomer$
      .pipe(takeUntil(this.destroyed$))
      .subscribe((customer) => {
        this.customer$.next(customer);
        this.initializeCustomCriteria();
      });
    this.permissionService.isCustomerAdmin$
      .pipe(takeUntil(this.destroyed$))
      .subscribe((isCustomerAdmin) => {
        if (!isCustomerAdmin) {
          this.historyBack();
        }
      });
  }

  initializeCustomCriteria() {
    if (!this.customer$.value) return;

    let customCriteriaFromCustomer = this.customer$.value.customCriteria || [];

    if (this.customCriteria$.value.length === 0) {
      this.customCriteria$.next([...customCriteriaFromCustomer]);
    } else {
      let current = [...this.customCriteria$.value];
      customCriteriaFromCustomer.forEach((fromCustomer: CustomCriteria) => {
        let groupIndex = current.findIndex((c) => c.id === fromCustomer.id);
        if (groupIndex === -1) {
          current.push(fromCustomer);
        }
        if (
          this.initialCustomCriteria[groupIndex] &&
          current[groupIndex].displayName ===
            this.initialCustomCriteria[groupIndex].displayName
        ) {
          current[groupIndex] = fromCustomer;
        }
      });
      this.customCriteria$.next(current);
    }

    this.initialCustomCriteria = [
      ...JSON.parse(JSON.stringify(customCriteriaFromCustomer)),
    ];
  }

  ngOnDestroy() {
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  historyBack() {
    this.location.back();
  }

  addNewCustomCriteria() {
    const current = [...this.customCriteriaInCreation$.value];
    current.push({
      displayName: '',
      type: CustomCriteriaType.TOGGLE,
      defaultValueOnJob: false,
      multiple: false,
      options: [],
      userEditable: true,
      enableOnJobRequest: true,
    });
    this.customCriteriaInCreation$.next(current);
  }

  cancelNewCustomCriteria(index: number) {
    const current = [...this.customCriteriaInCreation$.value];
    current.splice(index, 1);
    this.customCriteriaInCreation$.next(current);
  }

  saveNewCustomCriteria(index: number) {
    const draft = this.customCriteriaInCreation$.value[index];
    if (!draft?.displayName?.trim() || !this.customer$.value) return;

    let newCriteria: CustomCriteria;
    if (draft.type === CustomCriteriaType.TOGGLE) {
      newCriteria = {
        id: randomId(),
        displayName: draft.displayName.trim(),
        type: CustomCriteriaType.TOGGLE,
        userEditable: draft.userEditable !== false,
        enableOnJobRequest: draft.enableOnJobRequest !== false,
        defaultValueOnJob: draft.defaultValueOnJob || false,
        defaultValueOnProfile: draft.defaultValueOnProfile || false,
        blockingToggle: false,
      };
    } else {
      newCriteria = {
        id: randomId(),
        displayName: draft.displayName.trim(),
        type: CustomCriteriaType.SELECT,
        multiple: draft.multiple,
        userEditable: draft.userEditable !== false,
        enableOnJobRequest: draft.enableOnJobRequest !== false,
        options:
          draft.options
            ?.filter((o) => o.displayName.trim())
            .map((o) => ({
              id: o.id || randomId(),
              displayName: o.displayName.trim(),
            })) || [],
      };
    }

    const current = [...this.customCriteria$.value];
    current.push(newCriteria);
    this.customCriteria$.next(current);

    const currentInCreation = [...this.customCriteriaInCreation$.value];
    currentInCreation.splice(index, 1);
    this.customCriteriaInCreation$.next(currentInCreation);

    Meteor.call(
      'addCustomCriteria',
      this.customer$.value._id,
      newCriteria,
      (error: Meteor.Error) => {
        if (error) {
          this.toastService.add({
            type: 'ERROR',
            message: error.error?.toString(),
          });
        } else {
          this.toastService.add({ type: 'SUCCESS', message: 'changesSaved' });
        }
      },
    );
  }

  deleteCustomCriteria(customCriteriaId: string) {
    if (!this.customer$.value || !customCriteriaId) return;
    const current = [...this.customCriteria$.value];
    let index = current.findIndex((c) => c.id === customCriteriaId);
    if (index === -1) return;
    current.splice(index, 1);
    this.customCriteria$.next(current);

    Meteor.call(
      'removeCustomCriteria',
      this.customer$.value._id,
      customCriteriaId,
      (error: Meteor.Error) => {
        if (error) {
          this.toastService.add({
            type: 'ERROR',
            message: error.error?.toString(),
          });
        } else {
          this.toastService.add({ type: 'SUCCESS', message: 'changesSaved' });
        }
      },
    );
  }

  cancelChanges(customCriteriaId: string) {
    let index = this.initialCustomCriteria.findIndex(
      (c) => c.id === customCriteriaId,
    );
    if (index === -1) return;

    const current = [...this.customCriteria$.value];
    current[index] = JSON.parse(
      JSON.stringify(this.initialCustomCriteria[index]),
    );
    this.customCriteria$.next(current);
  }

  saveCustomCriteria(customCriteriaId: string): void {
    let criterion = this.customCriteria$.value.find(
      (c) => c.id === customCriteriaId,
    );
    if (!this.customer$.value || !criterion) return;
    Meteor.call(
      'updateCustomCriteria',
      this.customer$.value._id,
      criterion,
      (error: Meteor.Error) => {
        if (error) {
          this.toastService.add({
            type: 'ERROR',
            message: error.error?.toString(),
          });
        } else {
          this.toastService.add({ type: 'SUCCESS', message: 'changesSaved' });
        }
      },
    );
  }

  addCriterionSelectOption(criterion: CustomCriteriaSelect): void {
    criterion.options = [
      ...criterion.options,
      { id: randomId(), displayName: '' },
    ];
    this.customCriteria$.next([...this.customCriteria$.value]);
  }

  removeCriterionSelectOption(
    criterion: CustomCriteriaSelect,
    optionIndex: number,
  ): void {
    criterion.options = criterion.options.filter((_, i) => i !== optionIndex);
    this.customCriteria$.next([...this.customCriteria$.value]);
  }

  addDraftSelectOption(draftIndex: number): void {
    const drafts = [...this.customCriteriaInCreation$.value];
    const draft = drafts[draftIndex];
    if (!draft || draft.type !== CustomCriteriaType.SELECT) return;
    draft.options = [
      ...(draft.options || []),
      { id: randomId(), displayName: '' },
    ];
    this.customCriteriaInCreation$.next(drafts);
  }

  removeDraftSelectOption(draftIndex: number, optionIndex: number): void {
    const drafts = [...this.customCriteriaInCreation$.value];
    const draft = drafts[draftIndex];
    if (!draft || draft.type !== CustomCriteriaType.SELECT) return;
    draft.options = draft.options?.filter((_, i) => i !== optionIndex) || [];
    this.customCriteriaInCreation$.next(drafts);
  }

  onDraftTypeChange(index: number, type: CustomCriteriaType): void {
    const drafts = [...this.customCriteriaInCreation$.value];
    const draft = drafts[index];
    if (!draft) return;
    if (
      type === CustomCriteriaType.SELECT &&
      (draft.options?.length || 0) === 0
    ) {
      draft.options = [{ id: randomId(), displayName: '' }];
    }
    this.customCriteriaInCreation$.next(drafts);
  }

  touchCustomCriteriaList(): void {
    this.customCriteria$.next([...this.customCriteria$.value]);
  }

  trackById(index: number, object: CustomCriteria): string {
    return object.id;
  }

  trackByOptionId(index: number, o: { id: string }): string {
    return o.id;
  }
}
